import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import HintManager from "./HintManager";
import LatexRenderer from "./LatexRenderer";
import MathEditor from "./MathEditor";
import DesmosGraph from "./DesmosGraph";
import Chat from "./Chat";
import { HINT_LEVELS } from "../data/hintResources";
import { normalizeProblemType } from "../utils/problemType";
import { parseMathText } from "../utils/parseMathText";
import { normalizeMathText } from "../utils/chatMath";
import { AttemptStates } from "../fsm/problemAttemptFsm";
import { hasHintCheckResponse } from "../utils/hintTiming";
import { buildFlashcardSuggestions } from "../utils/flashcardSuggestions";
import { buildDesmosExpressions } from "../utils/desmosParsing";
import { getFlashcardParts } from "../utils/flashcardText";
import { getFlashcardTypeLabel } from "../utils/flashcardLabels";
import {
  CHAT_MODES,
  getChatMode,
  getChatModeLabel,
  sanitizeGuidanceResponse,
} from "../utils/chatGuidance";

const PROBLEM_TYPES = [
  "Substitution",
  "Integration – by parts",
  "Trig integrals",
  "Trig substitution",
  "Partial fractions",
  "p-series",
  "Ratio test",
  "Series comparison",
  "Other",
];

const APPROACHES_BY_TYPE = {
  Substitution: ["u-sub", "Algebraic rewrite", "Chain rule reversal", "Other"],
  "Integration – by parts": [
    "LIATE choice",
    "Exponential × trig",
    "Log × polynomial",
    "Tabular IBP",
    "Other",
  ],
  "Trig integrals": [
    "Power reduction",
    "Product-to-sum",
    "Pythagorean identity",
    "Half-angle",
    "Other",
  ],
  "Trig substitution": [
    "x = a sin θ",
    "x = a tan θ",
    "x = a sec θ",
    "Triangle sketch",
    "Other",
  ],
  "Partial fractions": [
    "Distinct linear",
    "Repeated linear",
    "Irreducible quadratic",
    "Complete the square",
    "Other",
  ],
  "p-series": [
    "Direct p-test",
    "Comparison to 1/n^p",
    "Integral test",
    "Limit comparison",
    "Other",
  ],
  "Ratio test": [
    "Algebraic simplification",
    "Factorial handling",
    "Root extraction",
    "Series radius",
    "Other",
  ],
  "Series comparison": [
    "Limit comparison",
    "Direct comparison",
    "Asymptotic expansion",
    "p-series anchor",
    "Other",
  ],
  Other: ["Direct evaluation", "Pattern match", "Graph/intuition", "Other"],
};

const getApproachesForType = (type) =>
  APPROACHES_BY_TYPE[type] || APPROACHES_BY_TYPE.Other;

const formatDate = (timestamp) => {
  if (!timestamp) return "Unknown";
  return new Date(timestamp).toLocaleString();
};

const isLikelyImageUrl = (value) => {
  if (!value) return false;
  const trimmed = String(value).trim();
  if (!trimmed) return false;
  if (trimmed.startsWith("data:image/")) return true;
  if (!/^https?:\/\//i.test(trimmed)) return false;
  return /\.(png|jpe?g|gif|webp|bmp|svg)(\?|#|$)/i.test(trimmed);
};

const extractSingleLatex = (statement) => {
  if (!statement) return "";
  const parts = parseMathText(statement);
  if (parts.length === 1 && parts[0].type === "latex") {
    return String(parts[0].value || "").trim();
  }
  return "";
};

const wrapStatementLatex = (statement, latex) => {
  const trimmed = String(statement || "").trim();
  if (!latex) return "";
  if (!trimmed) return latex;
  if (trimmed.startsWith("$$") && trimmed.endsWith("$$")) {
    return `$$${latex}$$`;
  }
  if (trimmed.startsWith("\\[") && trimmed.endsWith("\\]")) {
    return `\\[${latex}\\]`;
  }
  if (trimmed.startsWith("\\(") && trimmed.endsWith("\\)")) {
    return `\\(${latex}\\)`;
  }
  if (trimmed.startsWith("$") && trimmed.endsWith("$")) {
    return `$${latex}$`;
  }
  return `$$${latex}$$`;
};

const ProblemPanel = ({
  task,
  problem,
  onStart,
  onFinish,
  onCloseReview,
  onRecordHint,
  onUpdate,
  onReportLatexMismatch,
  hints = [],
  unlockedHintLevels = [],
  onAnalyzeText,
  onAnalyzeImage,
  onOcrLatexFromImage,
  onCheckAnswerForProblem,
  onSubmitAttemptPlan,
  onSubmitHintCheck,
  onRequestHint,
  onRequestDesmos,
  onAnswerChat,
  onCreateFlashcard,
  variant = "active",
  isBreakMode = false,
}) => {
  const isReview = variant === "review";
  const [problemType, setProblemType] = useState("Substitution");
  const [approach, setApproach] = useState("u-sub");
  const [difficultyScore, setDifficultyScore] = useState(3);
  const [whyType, setWhyType] = useState("");
  const [whyApproach, setWhyApproach] = useState("");
  const [pastedProblem, setPastedProblem] = useState("");
  const [pastedImage, setPastedImage] = useState(null);
  const [latex, setLatex] = useState("");
  const [statementDraft, setStatementDraft] = useState("");
  const [finalReflection, setFinalReflection] = useState("");
  const [mistakeTags, setMistakeTags] = useState("");
  const [confidence, setConfidence] = useState(3);
  const [autoComplete, setAutoComplete] = useState(false);
  const [saving, setSaving] = useState(false);
  const [answerImage, setAnswerImage] = useState(null);
  const [studentAnswerLatex, setStudentAnswerLatex] = useState("");
  const [answerCheck, setAnswerCheck] = useState(null);
  const [answerBusy, setAnswerBusy] = useState(false);
  const [pendingStatus, setPendingStatus] = useState("");
  const [answerStatus, setAnswerStatus] = useState("");
  const [planText, setPlanText] = useState("");
  const [dontKnowPreset, setDontKnowPreset] = useState("");
  const [dontKnowReason, setDontKnowReason] = useState("");
  const [gateSubmitting, setGateSubmitting] = useState(false);
  const [gateSubmitted, setGateSubmitted] = useState(false);
  const [gateNotice, setGateNotice] = useState("");
  const [intakeGateActive, setIntakeGateActive] = useState(false);
  const [commitOpen, setCommitOpen] = useState(false);
  const [commitAnswer, setCommitAnswer] = useState("");
  const [commitReasoning, setCommitReasoning] = useState("");
  const [commitConfidence, setCommitConfidence] = useState(70);
  const [commitError, setCommitError] = useState("");
  const [commitSubmitting, setCommitSubmitting] = useState(false);
  const [addedSuggestionIds, setAddedSuggestionIds] = useState([]);
  const [suggestionBusyId, setSuggestionBusyId] = useState(null);
  const [showVisualize, setShowVisualize] = useState(false);
  const [guidanceOpen, setGuidanceOpen] = useState(false);
  const [guidanceConversation, setGuidanceConversation] = useState([]);
  const [guidanceBusy, setGuidanceBusy] = useState(false);
  const [guidanceError, setGuidanceError] = useState("");
  const [inlineLatexEditorOpen, setInlineLatexEditorOpen] = useState(false);
  const [statementEditing, setStatementEditing] = useState(false);
  const [hintRequesting, setHintRequesting] = useState(false);
  const [hintRequestError, setHintRequestError] = useState("");
  const [desmosRequesting, setDesmosRequesting] = useState(false);
  const [desmosRequestError, setDesmosRequestError] = useState("");

  const lastSeededProblemIdRef = useRef(null);
  const autoUnlockRef = useRef(null);
  const prefetchedHintLevelsRef = useRef(new Set());
  const prefetchingHintLevelsRef = useRef(new Set());
  const lastDesmosRequestRef = useRef({ id: null, signature: null });

  useEffect(() => {
    const currentId = problem?.id || null;
    if (lastSeededProblemIdRef.current === currentId) return;
    lastSeededProblemIdRef.current = currentId;
    autoUnlockRef.current = null;

    if (!problem) {
      setPastedProblem("");
      setPastedImage(null);
      setLatex("");
      setStatementDraft("");
      setSaving(false);
      setAnswerImage(null);
      setStudentAnswerLatex("");
      setAnswerCheck(null);
      setPendingStatus("");
      setAnswerStatus("");
      setPlanText("");
      setDontKnowPreset("");
      setDontKnowReason("");
      setGateSubmitting(false);
      setGateSubmitted(false);
      setGateNotice("");
      setIntakeGateActive(false);
      setCommitOpen(false);
      setCommitAnswer("");
      setCommitReasoning("");
      setCommitConfidence(70);
      setCommitError("");
      setCommitSubmitting(false);
      setAddedSuggestionIds([]);
      setSuggestionBusyId(null);
      setShowVisualize(false);
      setGuidanceOpen(false);
      setGuidanceConversation([]);
      setGuidanceBusy(false);
      setGuidanceError("");
      return;
    }
    const rawType = problem.problemType || "";
    const normalizedType = normalizeProblemType(rawType);
    const nextType = PROBLEM_TYPES.includes(normalizedType)
      ? normalizedType
      : rawType
      ? "Other"
      : "Substitution";
    setProblemType(nextType);
    const fallbackApproach = getApproachesForType(nextType)[0] || "Other";
    setApproach(problem.approach || fallbackApproach);
    setDifficultyScore(
      typeof problem.difficultyScore === "number"
        ? problem.difficultyScore
        : 3
    );
    setWhyType(problem.whyType || "");
    setWhyApproach(problem.whyApproach || "");
    setFinalReflection(problem.finalReflection || "");
    setMistakeTags((problem.mistakeTags || []).join(", "));
    setConfidence(
      typeof problem.confidence === "number" ? problem.confidence : 3
    );
    setPastedProblem("");
    setPastedImage(null);
    setStatementDraft(problem.statement || problem.problemText || "");
    const fallbackLatex = extractSingleLatex(
      problem.statement || problem.problemText || ""
    );
    setLatex(problem.latex || fallbackLatex || "");
    setAnswerImage(null);
    setStudentAnswerLatex(problem.studentAnswerLatex || "");
    setAnswerCheck(problem.answerCheck || null);
    setCommitAnswer(
      typeof problem.commitAnswer === "string"
        ? problem.commitAnswer
        : problem.studentAnswerLatex || ""
    );
    setCommitReasoning(problem.commitReasoning || "");
    setCommitConfidence(
      typeof problem.commitConfidence === "number"
        ? problem.commitConfidence
        : 70
    );
    setAddedSuggestionIds([]);
    setSuggestionBusyId(null);
    setPendingStatus("");
    setAnswerStatus("");
    const fallbackPlanParts = [
      problem.planMethodGuess,
      problem.planWhy,
      problem.planFirstMove,
    ]
      .map((value) => String(value || "").trim())
      .filter(Boolean);
    setPlanText(problem.planText || fallbackPlanParts.join(" "));
    setDontKnowPreset("");
    setDontKnowReason(problem.planDontKnowReason || "");
    setGateSubmitting(false);
    setGateSubmitted(false);
    setGateNotice("");
    setIntakeGateActive(false);
    setCommitOpen(false);
    setCommitError("");
    setCommitSubmitting(false);
    setShowVisualize(false);
    setGuidanceOpen(false);
    setGuidanceConversation([]);
    setGuidanceBusy(false);
    setGuidanceError("");
    prefetchedHintLevelsRef.current = new Set();
    prefetchingHintLevelsRef.current = new Set();
  }, [problem]);

  const isInteractionLocked = !!isBreakMode;
  const reviewOpen = !!problem?.reviewOpen;
  const showReview = isReview || reviewOpen;
  const attemptState = problem?.attemptState || null;
  const planRecorded = !!problem?.planSubmittedAt;
  const gateRequired =
    !showReview &&
    !!problem &&
    [AttemptStates.GATE_OPEN, AttemptStates.OCR_PENDING, AttemptStates.INTAKE].includes(
      attemptState
    );
  const gateVisible =
    ((!problem && intakeGateActive) || gateRequired) && !planRecorded;
  const gateDisabled = isInteractionLocked || gateSubmitting;
  const showGatePanel = gateVisible && !gateSubmitted;
  const showGateNotice = gateVisible && gateSubmitted;
  const trimmedPlanText = planText.trim();
  const trimmedDontKnow = dontKnowReason.trim();
  const selectedDontKnow = dontKnowPreset.trim();
  const canSubmitPlan = !!trimmedPlanText;
  const canSubmitDontKnow =
    (!!selectedDontKnow && selectedDontKnow !== "Other") || !!trimmedDontKnow;
  const dontKnowValue =
    selectedDontKnow && selectedDontKnow !== "Other"
      ? selectedDontKnow
      : trimmedDontKnow;

  const statusTokens = useMemo(() => {
    const tokens = [];
    if (pendingStatus) tokens.push(pendingStatus);
    if (answerStatus) tokens.push(answerStatus);
    if (problem?.hintsGenerating) tokens.push("Generating hints…");
    if (saving && !pendingStatus) tokens.push("Saving…");
    return tokens;
  }, [answerStatus, pendingStatus, problem?.hintsGenerating, saving]);

  const [showLatexDetails, setShowLatexDetails] = useState(showReview);

  useEffect(() => {
    setShowLatexDetails(showReview);
  }, [showReview, problem?.id]);

  const guidanceMode = getChatMode({
    isBreakMode,
    planSubmittedAt: planRecorded,
    showReview,
  });
  const guidanceModeLabel = getChatModeLabel(guidanceMode);
  const guidanceDisabled = guidanceMode === CHAT_MODES.DISABLED || !onAnswerChat;
  const guidanceDisabledReason = (() => {
    if (!problem) return "Start a problem to enable guidance.";
    if (!onAnswerChat) return "Guidance is unavailable.";
    if (isBreakMode) return "Guidance is paused during breaks.";
    if (!planRecorded) return "Submit your plan to enable guidance.";
    return "";
  })();

  const handleStart = async (event, options = {}) => {
    event?.preventDefault?.();
    if (!task?.id) return;
    if (isInteractionLocked) return;
    const rawProblemPayload = (pastedProblem || options.rawProblem || "").trim();
    const rawProblemFile = pastedImage || options.rawProblemFile || null;
    const useImageUrl =
      !rawProblemFile && rawProblemPayload && isLikelyImageUrl(rawProblemPayload);
    setSaving(true);
    try {
      if (rawProblemFile && typeof onAnalyzeImage === "function") {
        setIntakeGateActive(true);
        setGateSubmitted(false);
        setGateNotice("");
        setPendingStatus("Analyzing image…");
        await onAnalyzeImage(task.id, rawProblemFile);
        return;
      }
      if (useImageUrl && typeof onAnalyzeImage === "function") {
        setIntakeGateActive(true);
        setGateSubmitted(false);
        setGateNotice("");
        setPendingStatus("Analyzing image…");
        await onAnalyzeImage(task.id, rawProblemPayload);
        return;
      }
      if (rawProblemPayload && typeof onAnalyzeText === "function") {
        setIntakeGateActive(true);
        setGateSubmitted(false);
        setGateNotice("");
        setPendingStatus("Analyzing text…");
        await onAnalyzeText(task.id, rawProblemPayload);
        return;
      }
      if (typeof onStart === "function") {
        setPendingStatus("Starting problem…");
        await onStart(task.id, {
          problemType,
          approach,
          difficultyScore,
          whyType,
          whyApproach,
          latex,
          rawProblem: rawProblemPayload,
          rawProblemFile,
          ...options,
        });
      }
    } finally {
      setPendingStatus("");
      setSaving(false);
    }
  };

  const hintTimeline = useMemo(() => problem?.hintTimeline || [], [problem]);
  const hintCheckResponses = useMemo(
    () => (Array.isArray(problem?.hintCheckResponses) ? problem.hintCheckResponses : []),
    [problem?.hintCheckResponses]
  );
  const loggedHintLevels = useMemo(
    () =>
      new Set(
        hintTimeline
          .map((entry) => entry.level)
          .filter((level) => typeof level === "number")
      ),
    [hintTimeline]
  );

  const nextHintLevel = useMemo(() => {
    const pending = HINT_LEVELS.find(
      (entry) => !loggedHintLevels.has(entry.level)
    );
    return pending?.level ?? null;
  }, [loggedHintLevels]);

  const nextHintTimeReady = useMemo(() => {
    if (typeof nextHintLevel !== "number") return false;
    return Array.isArray(unlockedHintLevels) && unlockedHintLevels.includes(nextHintLevel);
  }, [nextHintLevel, unlockedHintLevels]);

  const nextHintCheckReady = useMemo(() => {
    if (typeof nextHintLevel !== "number" || nextHintLevel <= 1) return true;
    return hasHintCheckResponse(hintCheckResponses, nextHintLevel - 1);
  }, [hintCheckResponses, nextHintLevel]);
  const hasUnlockedAnyHint = loggedHintLevels.size > 0;
  const actionDisabled = saving || isInteractionLocked || commitSubmitting;

  const latestCheckByLevel = useMemo(() => {
    return hintCheckResponses.reduce((acc, entry) => {
      const level = Number(entry?.level);
      if (!Number.isFinite(level)) return acc;
      const current = acc[level];
      if (!current || (entry.timestamp || 0) >= (current.timestamp || 0)) {
        acc[level] = entry;
      }
      return acc;
    }, {});
  }, [hintCheckResponses]);

  const hintByLevel = useMemo(() => {
    const entries = Array.isArray(hints) ? hints : [];
    return entries.reduce((acc, hint) => {
      const level = Number(hint?.level);
      if (!Number.isFinite(level)) return acc;
      acc.set(level, hint);
      return acc;
    }, new Map());
  }, [hints]);

  const buildPlanSummary = useCallback(() => {
    const parts = [];
    const planTextValue = problem?.planText || planText;
    if (planTextValue) parts.push(`Plan: ${planTextValue}`);
    if (problem?.planMethodGuess) parts.push(`Method: ${problem.planMethodGuess}`);
    if (problem?.planWhy) parts.push(`Why: ${problem.planWhy}`);
    if (problem?.planFirstMove) parts.push(`First move: ${problem.planFirstMove}`);

    const dontKnowValue = problem?.planDontKnowReason || dontKnowReason || dontKnowPreset;
    if (!parts.length && dontKnowValue) {
      parts.push(`Stuck: ${dontKnowValue}`);
    }

    return parts.join(" | ").trim();
  }, [dontKnowPreset, dontKnowReason, planText, problem?.planDontKnowReason, problem?.planFirstMove, problem?.planMethodGuess, problem?.planText, problem?.planWhy]);

  const hasHintContent = useCallback(
    (level) => {
      if (!Number.isFinite(level)) return false;
      const hint = hintByLevel.get(level);
      return !!hint?.text?.trim();
    },
    [hintByLevel]
  );

  const getHintResourceId = useCallback(
    (level) => {
      const hint = hintByLevel.get(level);
      return hint?.resourceId || null;
    },
    [hintByLevel]
  );

  const nextHintUnlockNote = useMemo(() => {
    if (typeof nextHintLevel !== "number") return "";
    if (!nextHintTimeReady) return "Hint not yet eligible.";
    if (!nextHintCheckReady) return "Submit your progress update to unlock the next hint.";
    return "Hint ready.";
  }, [nextHintCheckReady, nextHintLevel, nextHintTimeReady]);

  const handleRequestHint = async () => {
    if (!problem?.id || !onRequestHint) return;
    if (isInteractionLocked || hintRequesting) return;
    if (typeof nextHintLevel !== "number") return;
    if (!nextHintTimeReady || !nextHintCheckReady) return;

    const level = nextHintLevel;
    const isFirstHint = level === 1;
    const planSummary = buildPlanSummary();
    const hasRecordedPlan = !!problem?.planSubmittedAt || !!problem?.planDontKnowReason;
    const previousResponse =
      level > 1 ? latestCheckByLevel[level - 1]?.response || "" : "";
    const previousResponseType =
      level > 1 ? latestCheckByLevel[level - 1]?.responseType || "text" : "plan";

    if (hasHintContent(level) && onRecordHint) {
      setHintRequestError("");
      setPendingStatus(`Unlocking hint ${level}...`);
      try {
        await onRecordHint(problem.id, level, getHintResourceId(level));
      } catch (error) {
        console.error("Failed to unlock hint", error);
        setHintRequestError("Unable to unlock the hint right now.");
      } finally {
        setPendingStatus("");
      }
      return;
    }

    if (isFirstHint && !planSummary) {
      if (hasRecordedPlan) {
        // Plan exists but summary is empty; fall back to a generic marker.
        setHintRequestError("");
      } else {
        setHintRequestError("Submit a plan before requesting the first hint.");
        return;
      }
    }
    if (!isFirstHint && !previousResponse && previousResponseType !== "dont_know") {
      setHintRequestError("Add a progress update before requesting the next hint.");
      return;
    }

    setHintRequesting(true);
    setHintRequestError("");
    try {
      setPendingStatus(`Requesting hint ${level}...`);
      await onRequestHint(problem.id, level, {
        response: isFirstHint
          ? planSummary || "Plan submitted."
          : previousResponse,
        responseType: isFirstHint ? "plan" : previousResponseType,
        planText: planSummary,
        problemText: effectiveProblemText,
      });
    } catch (error) {
      console.error("Failed to request hint", error);
      setHintRequestError("Unable to request the hint right now.");
    } finally {
      setPendingStatus("");
      setHintRequesting(false);
    }
  };

  const handleSaveMetadata = async () => {
    if (!onUpdate || !problem?.id) return;
    setSaving(true);
    try {
      await onUpdate(problem.id, {
        problemType,
        approach,
        difficultyScore,
        whyType,
        whyApproach,
        latex,
        finalReflection,
        mistakeTags: mistakeTags.split(",").map(s => s.trim()).filter(Boolean),
        confidence,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCopyLatex = async (value = latex) => {
    const text = String(value || "").trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error("Failed to copy", err);
    }
  };

  const renderAttemptGate = () => (
    <div className="problem-panel__gate card-quiet">
      <div className="task-form__field">
        <label>Plan your attack</label>
        <div className="mini-label" style={{ marginBottom: "6px" }}>
          Keep it short and actionable.
        </div>
        <textarea
          rows={3}
          value={planText}
          onChange={(e) => setPlanText(e.target.value)}
          placeholder="Method + why + first move"
          disabled={gateDisabled}
        />
      </div>
      <div className="task-form__row">
        <div className="task-form__field" style={{ maxWidth: "240px" }}>
          <label>Stuck reason (pick one)</label>
          <select
            value={dontKnowPreset}
            onChange={(e) => setDontKnowPreset(e.target.value)}
            disabled={gateDisabled}
          >
            <option value="">Select</option>
            <option value="No idea where to start">No idea where to start</option>
            <option value="Don't recognize the pattern">Don't recognize the pattern</option>
            <option value="OCR is unclear">OCR is unclear</option>
            <option value="Missing prerequisite">Missing prerequisite</option>
            <option value="Other">Other</option>
          </select>
        </div>
        <div className="task-form__field">
          <label>Short reason (if no preset)</label>
          <input
            type="text"
            value={dontKnowReason}
            onChange={(e) => setDontKnowReason(e.target.value)}
            placeholder="Short reason"
            disabled={gateDisabled}
          />
        </div>
      </div>
      {gateNotice && (
        <p className="mini-label" style={{ margin: "4px 0 0" }}>
          {gateNotice}
        </p>
      )}
      <div className="cluster" style={{ gap: "8px", marginTop: "10px" }}>
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => handleSubmitGate("plan")}
          disabled={gateDisabled || !canSubmitPlan}
        >
          {gateSubmitting ? "Saving..." : "Submit Plan"}
        </button>
        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => handleSubmitGate("dont_know")}
          disabled={gateDisabled || !canSubmitDontKnow}
        >
          {gateSubmitting ? "Saving..." : "I don't know"}
        </button>
      </div>
    </div>
  );

  const isImageProblem =
    typeof problem?.rawProblem === "string" &&
    (problem.rawProblem.startsWith("http") ||
      problem.rawProblem.startsWith("data:image/"));
  const showOcrVerification =
    !showReview &&
    isImageProblem &&
    !!latex?.trim() &&
    problem?.latexVerified !== true;
  const showHintActionRow =
    !showReview && (isImageProblem || problem?.hintsGenerating);
  const showSolvedActionRow =
    !showReview &&
    !isImageProblem &&
    !problem?.hintsGenerating;
  const hintActionNotes = [
    isImageProblem
      ? "If the extracted LaTeX does not match the image, retry the analysis."
      : null,
    problem?.hintsGenerating ? "Generating hints..." : null,
  ]
    .filter(Boolean)
    .join(" ");

  const hasPrefetchedNextHint = useMemo(() => {
    if (typeof nextHintLevel !== "number") return false;
    return hasHintContent(nextHintLevel);
  }, [hasHintContent, nextHintLevel]);

  const showHintRequestRow =
    !showReview && typeof nextHintLevel === "number";

  const reviewAnswerCheck = answerCheck || problem?.answerCheck || null;
  const commitSummary = {
    answer:
      typeof problem?.commitAnswer === "string"
        ? problem.commitAnswer
        : commitAnswer,
    reasoning:
      typeof problem?.commitReasoning === "string"
        ? problem.commitReasoning
        : commitReasoning,
    confidence:
      typeof problem?.commitConfidence === "number"
        ? problem.commitConfidence
        : commitConfidence,
    submittedAt:
      typeof problem?.commitSubmittedAt === "number"
        ? problem.commitSubmittedAt
        : null,
  };
  const canonicalSteps = Array.isArray(problem?.canonicalSolution?.steps)
    ? problem.canonicalSolution.steps
    : [];
  const suggestedCards = useMemo(
    () => buildFlashcardSuggestions(problem),
    [problem]
  );
  const canAddMoreCards = addedSuggestionIds.length < 2;

  const handleAddSuggestion = async (suggestion) => {
    if (!onCreateFlashcard || !problem?.id) return;
    if (addedSuggestionIds.includes(suggestion.id)) return;
    if (!canAddMoreCards) return;
    setSuggestionBusyId(suggestion.id);
    try {
      await onCreateFlashcard({
        front: suggestion.front,
        back: suggestion.back,
        type: suggestion.type,
        tags: suggestion.tags || [],
        sourceProblemId: problem.id,
        sourceAttemptId: problem.attemptId || null,
        problemType: problem.problemType || "",
        origin: "suggested",
      });
      setAddedSuggestionIds((prev) => [...prev, suggestion.id]);
    } catch (error) {
      console.error("Failed to create flashcard", error);
    } finally {
      setSuggestionBusyId(null);
    }
  };

  const statementCandidate = useMemo(() => {
    const explicit = typeof problem?.statement === "string" ? problem.statement.trim() : "";
    if (explicit) return explicit;

    const rawText = typeof problem?.problemText === "string" ? problem.problemText.trim() : "";
    if (rawText) return rawText;

    const fallback = typeof problem?.latex === "string" ? problem.latex.trim() : "";
    const looksDelimited = /(\$\$|\\\[|\\\(|\$)/.test(fallback);
    return looksDelimited ? fallback : "";
  }, [problem?.latex, problem?.problemText, problem?.statement]);

  useEffect(() => {
    if (statementEditing) return;
    setStatementDraft(statementCandidate || "");
  }, [statementCandidate, statementEditing]);

  const statementParts = useMemo(
    () => parseMathText(statementDraft || ""),
    [statementDraft]
  );

  const effectiveProblemText = useMemo(() => {
    const draft = String(statementDraft || "").trim();
    if (draft) return draft;
    return String(problem?.statement || problem?.problemText || "").trim();
  }, [problem?.problemText, problem?.statement, statementDraft]);

  const prefetchHint = useCallback(
    async ({ level, response, responseType, planText: planTextValue }) => {
      if (!problem?.id || !onRequestHint) return;
      if (!Number.isFinite(level) || level <= 0 || level > HINT_LEVELS.length) return;
      if (hasHintContent(level)) return;
      if (prefetchedHintLevelsRef.current.has(level)) return;
      if (prefetchingHintLevelsRef.current.has(level)) return;
      prefetchingHintLevelsRef.current.add(level);
      try {
        await onRequestHint(problem.id, level, {
          response: response || "",
          responseType: responseType || "text",
          planText: planTextValue || "",
          problemText: effectiveProblemText,
          prefetchOnly: true,
        });
        prefetchedHintLevelsRef.current.add(level);
      } catch (error) {
        console.error("Failed to prefetch hint", error);
      } finally {
        prefetchingHintLevelsRef.current.delete(level);
      }
    },
    [effectiveProblemText, hasHintContent, onRequestHint, problem?.id]
  );

  const statementHasText = useMemo(
    () =>
      statementParts.some(
        (part) => part.type === "text" && part.value.trim().length > 0
      ),
    [statementParts]
  );

  const statementIsSingleLatex =
    statementParts.length === 1 && statementParts[0].type === "latex";

  const statementSingleLatexValue =
    statementIsSingleLatex && statementParts[0]?.type === "latex"
      ? String(statementParts[0].value || "").trim()
      : "";
  const previewLatexValue =
    typeof latex === "string" && latex.trim()
      ? latex
      : String(statementSingleLatexValue || "");
  const hasEditableLatex = !!previewLatexValue.trim();

  const previewMode =
    statementHasText || statementDraft
      ? "statement"
      : hasEditableLatex
      ? "latex"
      : "statement";

  useEffect(() => {
    if (!problem?.id) {
      setInlineLatexEditorOpen(false);
      setStatementEditing(false);
      return;
    }
    setInlineLatexEditorOpen(!statementHasText && hasEditableLatex);
  }, [hasEditableLatex, problem?.id, statementHasText]);

  useEffect(() => {
    if (!commitOpen) return undefined;
    document.body.classList.add("modal-open");
    return () => {
      document.body.classList.remove("modal-open");
    };
  }, [commitOpen]);

  const previewLength =
    previewMode === "statement"
      ? statementDraft.length
      : previewLatexValue.length;

  const hasDetailsSource = !!(statementDraft?.trim() || latex?.trim());
  const problemMediaHeight = useMemo(() => {
    const base = "clamp(170px, 26vh, 260px)";
    if (showReview) {
      // Review panels can afford more space; reduce clipping on word problems.
      if (previewMode === "statement") return "clamp(220px, 36vh, 480px)";
      return "clamp(190px, 30vh, 320px)";
    }

    if (previewMode === "statement") {
      if (previewLength > 320) return "clamp(280px, 46vh, 600px)";
      if (previewLength > 200) return "clamp(250px, 40vh, 520px)";
      if (previewLength > 120) return "clamp(220px, 34vh, 440px)";
      return "clamp(190px, 30vh, 340px)";
    }

    if (previewLength > 260) return "clamp(210px, 32vh, 420px)";
    if (previewLength > 160) return "clamp(190px, 30vh, 340px)";
    return base;
  }, [previewLength, previewMode, showReview]);

  const renderStatementParts = () =>
    statementParts.map((part, index) => {
      if (part.type === "latex") {
        const Wrapper = part.displayMode ? "div" : "span";
        const className = part.displayMode
          ? "problem-panel__statement-latex problem-panel__statement-latex--block"
          : "problem-panel__statement-latex problem-panel__statement-latex--inline";
        return (
          <Wrapper key={`statement-latex-${index}`} className={className}>
            <LatexRenderer
              latex={part.value}
              displayMode={part.displayMode}
              fitToContainer={part.displayMode}
            />
          </Wrapper>
        );
      }
      return (
        <span key={`statement-text-${index}`}>{part.value}</span>
      );
    });

  const renderRawProblemParts = (text) => {
    const normalized = normalizeMathText(text || "");
    const parts = parseMathText(normalized);
    if (!parts.length) {
      return <pre style={{ whiteSpace: "pre-wrap" }}>{text}</pre>;
    }
    return parts.map((part, index) => {
      if (part.type === "latex") {
        const Wrapper = part.displayMode ? "div" : "span";
        const className = part.displayMode
          ? "problem-panel__statement-latex problem-panel__statement-latex--block"
          : "problem-panel__statement-latex problem-panel__statement-latex--inline";
        return (
          <Wrapper key={`raw-latex-${index}`} className={className}>
            <LatexRenderer
              latex={part.value}
              displayMode={part.displayMode}
              fitToContainer={part.displayMode}
            />
          </Wrapper>
        );
      }
      return <span key={`raw-text-${index}`}>{part.value}</span>;
    });
  };

  const renderFlashcardText = (text, keyPrefix) => {
    const parts = getFlashcardParts(text || "");
    return parts.map((part, index) => {
      if (part.type === "latex") {
        const className = part.displayMode
          ? "flashcard-math flashcard-math--block"
          : "flashcard-math flashcard-math--inline";
        const Wrapper = part.displayMode ? "div" : "span";
        return (
          <Wrapper key={`${keyPrefix}-latex-${index}`} className={className}>
            <LatexRenderer
              latex={part.value}
              displayMode={part.displayMode}
            />
          </Wrapper>
        );
      }
      return <span key={`${keyPrefix}-text-${index}`}>{part.value}</span>;
    });
  };

  const showStatementDetails =
    !!statementDraft?.trim() && statementHasText;
  const latexLooksDelimited = /(\$\$|\\\[|\\\(|\$)/.test(String(latex || ""));
  const showExpressionDetails =
    !!latex?.trim() &&
    !latexLooksDelimited &&
    (!statementSingleLatexValue ||
      latex.trim() !== statementSingleLatexValue);

  const desmosSourceLatex = useMemo(() => {
    if (latex?.trim()) return latex;
    if (statementSingleLatexValue?.trim()) return statementSingleLatexValue;
    return "";
  }, [latex, statementSingleLatexValue]);

  const canVisualize = !!problem && (showReview || !gateVisible);
  const desmosExpressions = useMemo(() => {
    const suggested = Array.isArray(problem?.desmosExpressions)
      ? problem.desmosExpressions
      : [];
    if (suggested.length) return suggested;
    const overlays =
      showReview && Array.isArray(problem?.desmosOverlays)
        ? problem.desmosOverlays
        : [];
    return buildDesmosExpressions({
      latex: desmosSourceLatex,
      overlays,
    });
  }, [desmosSourceLatex, problem?.desmosExpressions, problem?.desmosOverlays, showReview]);

  useEffect(() => {
    if (!showVisualize || !canVisualize) return;
    if (!onRequestDesmos || !problem?.id) return;
    if (Array.isArray(problem?.desmosExpressions) && problem.desmosExpressions.length) return;

    const problemTextSource =
      problem?.problemText || problem?.statement || problem?.rawProblem || "";
    const signature = `${problem.id}|${problemTextSource}|${desmosSourceLatex}`;
    if (lastDesmosRequestRef.current.signature === signature) return;

    lastDesmosRequestRef.current = { id: problem.id, signature };
    setDesmosRequesting(true);
    setDesmosRequestError("");
    Promise.resolve(onRequestDesmos(problem.id))
      .catch((error) => {
        const message = error instanceof Error ? error.message : String(error || "");
        setDesmosRequestError(message || "Failed to generate graph suggestions.");
      })
      .finally(() => {
        setDesmosRequesting(false);
      });
  }, [
    showVisualize,
    canVisualize,
    onRequestDesmos,
    problem?.id,
    problem?.problemText,
    problem?.statement,
    problem?.rawProblem,
    problem?.desmosExpressions,
    desmosSourceLatex,
  ]);

  const handleInlineLatexChange = (value) => {
    if (isInteractionLocked) return;
    setLatex(String(value || ""));
  };

  const handleInlineLatexCommit = async (value) => {
    if (!problem?.id || !onUpdate) return;
    if (isInteractionLocked) return;
    const nextLatex = String(value || "").trim();
    const currentLatex = String(problem?.latex || "").trim();
    const currentStatement = String(statementDraft || "").trim();
    const shouldUpdateStatement = statementIsSingleLatex && currentStatement;
    const nextStatement = shouldUpdateStatement
      ? wrapStatementLatex(statementDraft, nextLatex)
      : null;

    const hasLatexChange = nextLatex !== currentLatex;
    const hasStatementChange =
      shouldUpdateStatement && nextStatement && nextStatement !== currentStatement;

    if (!hasLatexChange && !hasStatementChange) return;

    setLatex(nextLatex);
    setPendingStatus("Saving edit…");
    try {
      const payload = { latex: nextLatex };
      if (shouldUpdateStatement && nextStatement) {
        payload.statement = nextStatement;
      }
      await onUpdate(problem.id, payload);
    } finally {
      setPendingStatus("");
    }
  };

  const handleStatementSave = async () => {
    if (!problem?.id || !onUpdate) return;
    if (isInteractionLocked) return;
    const nextStatement = String(statementDraft || "").trim();
    const currentStatement = String(problem?.statement || "");
    const currentProblemText = String(problem?.problemText || "");
    if (nextStatement === currentStatement && nextStatement === currentProblemText) return;
    setPendingStatus("Saving edit…");
    try {
      await onUpdate(problem.id, {
        statement: nextStatement,
        problemText: nextStatement,
      });
      setStatementEditing(false);
    } finally {
      setPendingStatus("");
    }
  };

  const handleStatementCancel = () => {
    setStatementDraft(statementCandidate || "");
    setStatementEditing(false);
  };

  const handleLatexMismatch = async () => {
    if (!onReportLatexMismatch || !problem?.id) return;
    if (isInteractionLocked) return;
    try {
      setPendingStatus("Re-analyzing image…");
      await onReportLatexMismatch(problem.id);
    } catch (error) {
      console.error("Failed to re-run analysis", error);
    } finally {
      setPendingStatus("");
    }
  };

  const handleLatexConfirmed = async () => {
    if (!onUpdate || !problem?.id) return;
    if (isInteractionLocked) return;
    try {
      setPendingStatus("Confirming OCR...");
      await onUpdate(problem.id, { latexVerified: true });
    } catch (error) {
      console.error("Failed to confirm OCR", error);
    } finally {
      setPendingStatus("");
    }
  };

  const handleSubmitGate = async (mode) => {
    if (!onSubmitAttemptPlan) return;
    if (gateDisabled) return;
    if (mode === "plan" && !canSubmitPlan) return;
    if (mode === "dont_know" && !canSubmitDontKnow) return;

    const payload = {
      planText: trimmedPlanText,
      dontKnowReason: dontKnowValue,
      mode,
    };

    let queued = false;
    setGateSubmitting(true);
    setGateNotice("");
    if (problem?.id) {
      setPendingStatus("Saving plan...");
    }
    try {
      const result = await onSubmitAttemptPlan(payload);
      queued = !!result?.queued;
      setGateSubmitted(true);
      setGateNotice(
        queued
          ? "Plan saved. Waiting for OCR..."
          : mode === "dont_know"
          ? "Marked as stuck."
          : "Plan submitted."
      );
      if (!queued && problem?.id) {
        const localPlanSummary = trimmedPlanText
          ? `Plan: ${trimmedPlanText}`
          : dontKnowValue
          ? `Stuck: ${dontKnowValue}`
          : "";
        void prefetchHint({
          level: 1,
          response: localPlanSummary || "Plan submitted.",
          responseType: mode === "dont_know" ? "dont_know" : "plan",
          planText: localPlanSummary,
        });
      }
    } catch (error) {
      console.error("Failed to submit plan", error);
      setGateNotice("Unable to save the plan right now.");
      setGateSubmitted(false);
    } finally {
      setGateSubmitting(false);
      if (problem?.id) {
        setPendingStatus("");
      }
    }
  };

  const handleGuidanceSend = async (message) => {
    if (guidanceDisabled || !onAnswerChat || !problem?.id) return;
    const sessionId = problem?.attemptId || problem?.id;
    const nextConversation = [
      ...guidanceConversation,
      { role: "user", content: message },
    ];
    setGuidanceConversation(nextConversation);
    setGuidanceBusy(true);
    setGuidanceError("");
    try {
      const assistantMessage = await onAnswerChat(
        problem.id,
        sessionId,
        nextConversation,
        message,
        { mode: guidanceMode, problemText: effectiveProblemText }
      );
      const safeMessage = sanitizeGuidanceResponse(
        assistantMessage,
        guidanceMode
      );
      setGuidanceConversation([
        ...nextConversation,
        { role: "assistant", content: safeMessage },
      ]);
    } catch (error) {
      console.error("Failed to fetch guidance", error);
      setGuidanceError("Unable to fetch guidance right now.");
    } finally {
      setGuidanceBusy(false);
    }
  };

  // Hint unlocks are user-triggered (no auto-reveal).

  const handleHintCheckSubmit = async (level, response, responseType) => {
    if (!problem?.id || !onSubmitHintCheck) return;
    if (isInteractionLocked) return;
    try {
      setPendingStatus(`Saving progress update for hint ${level}...`);
      await onSubmitHintCheck(problem.id, {
        level,
        response,
        responseType,
      });
      if (level < HINT_LEVELS.length) {
        void prefetchHint({
          level: level + 1,
          response,
          responseType,
          planText: buildPlanSummary(),
        });
      }
    } catch (error) {
      console.error("Failed to record hint progress update", error);
    } finally {
      setPendingStatus("");
    }
  };

  const handleFinish = () => {
    if (isInteractionLocked || showReview) return;
    setCommitError("");
    setCommitOpen(true);
    if (!commitAnswer.trim()) {
      setCommitAnswer(studentAnswerLatex || "");
    }
  };

  const handleCommitSubmit = async () => {
    if (!onFinish || !problem?.id) return;
    if (isInteractionLocked) return;

    const trimmedAnswer = String(commitAnswer || "").trim();
    const trimmedReasoning = String(commitReasoning || "").trim();

    if (!trimmedAnswer || !trimmedReasoning) {
      setCommitError("Add a final answer and a one-sentence reason to continue.");
      return;
    }

    setCommitSubmitting(true);
    setSaving(true);
    let checkResult = null;
    try {
      setPendingStatus("Checking answer…");
      if (onCheckAnswerForProblem) {
        try {
          const problemText =
            statementDraft ||
            problem.problemText ||
            problem.statement ||
            problem.rawProblem ||
            "";
          checkResult = await onCheckAnswerForProblem(problem.id, {
            problemText,
            problemLatex: latex || problem.latex || "",
            canonicalSolution: problem.canonicalSolution || null,
            studentAnswerLatex: trimmedAnswer,
          });
          setStudentAnswerLatex(trimmedAnswer);
        } catch (error) {
          console.error("Failed to check answer", error);
          checkResult = {
            correct: null,
            confidence: 0,
            feedback: "Unable to check answer automatically.",
          };
        }
      } else {
        checkResult = {
          correct: null,
          confidence: 0,
          feedback: "Answer check is unavailable in this build.",
        };
      }
      setAnswerCheck(checkResult || null);

      setPendingStatus("Saving solution…");
      await onFinish(problem.id, {
        taskId: task?.id,
        finalReflection,
        mistakeTags: mistakeTags
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        confidence,
        completeTask: autoComplete,
        commitAnswer: trimmedAnswer,
        commitReasoning: trimmedReasoning,
        commitConfidence,
        commitSubmittedAt: Date.now(),
        reviewOpen: true,
        keepActiveProblem: true,
        answerCheck: checkResult,
        answerCorrect:
          typeof checkResult?.correct === "boolean" ? checkResult.correct : null,
      });
      setCommitOpen(false);
    } catch (error) {
      console.error("Failed to commit solution", error);
      setCommitError("Unable to submit the commit right now.");
    } finally {
      setPendingStatus("");
      setCommitSubmitting(false);
      setSaving(false);
    }
  };

  const handleAnswerPasteEvent = (e) => {
    if (e.clipboardData && e.clipboardData.items) {
      const items = e.clipboardData.items;
      for (const item of items) {
        if (item.type.indexOf("image") !== -1) {
          const blob = item.getAsFile();
          setAnswerImage(blob);
          e.preventDefault();
          return;
        }
      }
    }
  };

  const handlePasteAnswer = async () => {
    if (navigator?.clipboard?.read) {
      try {
        const items = await navigator.clipboard.read();
        for (const item of items) {
          const imgType = item.types.find((t) => t.startsWith("image/"));
          if (imgType) {
            const blob = await item.getType(imgType);
            const ext = imgType.split("/")[1] || "png";
            const file = new File([blob], `answer-${Date.now()}.${ext}`, {
              type: imgType,
            });
            setAnswerImage(file);
            return;
          }
        }
      } catch (error) {
        console.warn("Clipboard read() failed, falling back to readText()", error);
      }
    }

    if (navigator?.clipboard?.readText) {
      const text = await navigator.clipboard.readText();
      if (text) {
        setStudentAnswerLatex((prev) => (prev ? `${prev}\n${text}` : text));
      }
    }
  };

  const handleExtractAnswerLatex = async () => {
    if (!problem?.id || !answerImage || !onOcrLatexFromImage) return;
    if (isInteractionLocked) return;
    setAnswerBusy(true);
    try {
      setAnswerStatus("Extracting answer…");
      const extracted = await onOcrLatexFromImage(answerImage);
      const nextLatex = extracted?.latex || "";
      setStudentAnswerLatex(nextLatex);
      setAnswerCheck(null);
      if (onUpdate) {
        await onUpdate(problem.id, { studentAnswerLatex: nextLatex });
      }
    } catch (error) {
      console.error("Failed to extract answer LaTeX", error);
    } finally {
      setAnswerStatus("");
      setAnswerBusy(false);
    }
  };

  const handleCheckAnswer = async () => {
    if (!problem?.id || !onCheckAnswerForProblem) return;
    if (isInteractionLocked) return;
    const trimmed = String(studentAnswerLatex || "").trim();
    if (!trimmed) return;
    setAnswerBusy(true);
    try {
      setAnswerStatus("Checking answer…");
      const problemText =
        statementDraft ||
        problem.problemText ||
        problem.statement ||
        problem.rawProblem ||
        "";
      const result = await onCheckAnswerForProblem(problem.id, {
        problemText,
        problemLatex: latex || problem.latex || "",
        canonicalSolution: problem.canonicalSolution || null,
        studentAnswerLatex: trimmed,
      });
      setAnswerCheck(result || null);
    } catch (error) {
      console.error("Failed to check answer", error);
      setAnswerCheck({
        correct: null,
        confidence: 0,
        feedback: "Unable to check answer automatically.",
      });
    } finally {
      setAnswerStatus("");
      setAnswerBusy(false);
    }
  };

  const handlePasteEvent = (e) => {
    if (e.clipboardData && e.clipboardData.items) {
      const items = e.clipboardData.items;
      for (const item of items) {
        if (item.type.indexOf("image") !== -1) {
          const blob = item.getAsFile();
          setPastedImage(blob);
          setPastedProblem("");
          e.preventDefault();
          return;
        }
      }
    }
  };


  const handlePaste = async () => {
    if (navigator?.clipboard?.read) {
      try {
        const items = await navigator.clipboard.read();
        for (const item of items) {
          const imgType = item.types.find((t) => t.startsWith("image/"));
          if (imgType) {
            const blob = await item.getType(imgType);
            const ext = imgType.split("/")[1] || "png";
            const file = new File([blob], `pasted-${Date.now()}.${ext}`, {
              type: imgType,
            });
            setPastedImage(file);
            setPastedProblem(""); // Clear text if image pasted
            return;
          }
        }
      } catch (error) {
        console.warn("Clipboard read() failed, falling back to readText()", error);
      }
    }

    if (navigator?.clipboard?.readText) {
      const text = await navigator.clipboard.readText();
      if (text) {
        setPastedProblem((prev) => (prev ? `${prev}\n${text}` : text));
      }
    }
  };

  // ...

  return (
    <div className="problem-panel">
      {!problem && (
        <form className="stack stack--dense" onSubmit={handleStart}>
          <div className="task-form__field">
            <label>Problem</label>
            <div className="mini-label" style={{ marginBottom: "6px" }}>
              Paste a prompt, image URL, or drop an image from your clipboard.
            </div>
            <div onPaste={handlePasteEvent}>
              {pastedImage ? (
                <div className="problem-panel__preview" style={{ marginBottom: "8px" }}>
                  <img
                    src={URL.createObjectURL(pastedImage)}
                    alt="Pasted"
                    style={{ maxWidth: "100%", borderRadius: "8px" }}
                  />
                  <button
                    type="button"
                    className="btn btn--ghost btn--small"
                    onClick={() => setPastedImage(null)}
                    style={{ marginTop: "4px" }}
                  >
                    Remove image
                  </button>
                </div>
              ) : (
                <textarea
                  rows={3}
                  value={pastedProblem}
                  onChange={(e) => setPastedProblem(e.target.value)}
                  placeholder="Paste copied snippet here"
                />
              )}
            </div>

            <div className="cluster" style={{ gap: "8px", marginTop: "8px" }}>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={handlePaste}
                disabled={isInteractionLocked}
              >
                Paste from clipboard
              </button>
              <button
                type="submit"
                className={`btn btn--primary btn--start${saving ? " btn--breathing" : ""}`}
                disabled={saving || isInteractionLocked}
              >
                {saving ? "Analyzing..." : "Start"}
              </button>
            </div>
            {pendingStatus && (
              <p className="mini-label" style={{ marginTop: "6px" }}>
                {pendingStatus}
              </p>
            )}
          </div>

          {showGatePanel && renderAttemptGate()}
          {showGateNotice && !showGatePanel && gateNotice && (
            <div className="problem-panel__gate card-quiet">
              <p className="mini-label" style={{ margin: 0 }}>
                {gateNotice}
              </p>
            </div>
          )}
        </form>
      )}

      {problem && (
        <div className="stack stack--dense">
          {problem.rawProblem && (
            <div
              className={`problem-panel__problem-row${
                previewMode === "statement"
                  ? " problem-panel__problem-row--statement"
                  : ""
              }`}
              style={{ "--problem-media-height": problemMediaHeight }}
            >
              <div className="problem-panel__preview">
                <p className="mini-label" style={{ margin: 0 }}>
                  Problem
                </p>
                <div className="problem-panel__preview-body">
                  {typeof problem.rawProblem === "string" &&
                  (problem.rawProblem.startsWith("http") ||
                    problem.rawProblem.startsWith("data:image/")) ? (
                    <img
                      src={problem.rawProblem}
                      alt="Problem"
                      style={{ maxWidth: "100%", borderRadius: "10px" }}
                    />
                  ) : (
                    <div className="problem-panel__statement">
                      {renderRawProblemParts(problem.rawProblem)}
                    </div>
                  )}
                </div>
              </div>
              <div className="problem-panel__latex-preview">
                {(hasDetailsSource || statementDraft?.trim() || hasEditableLatex) && (
                  <div className="problem-panel__latex-actions">
                    {hasDetailsSource && (
                      <button
                        type="button"
                        className="btn btn--ghost btn--small problem-panel__latex-toggle"
                        onClick={() => setShowLatexDetails((prev) => !prev)}
                        aria-expanded={showLatexDetails}
                      >
                        {showLatexDetails ? "Hide details" : "Details"}
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn--ghost btn--small problem-panel__latex-toggle"
                      onClick={() =>
                        setStatementEditing((prev) => {
                          const next = !prev;
                          if (next) setInlineLatexEditorOpen(false);
                          return next;
                        })
                      }
                      disabled={isInteractionLocked}
                    >
                      {statementEditing
                        ? "Hide editor"
                        : statementDraft?.trim()
                        ? "Edit statement"
                        : "Add statement"}
                    </button>
                    {hasEditableLatex && statementDraft?.trim() ? (
                      <button
                        type="button"
                        className="btn btn--ghost btn--small problem-panel__latex-toggle"
                        onClick={() => {
                          setInlineLatexEditorOpen((prev) => !prev);
                          setStatementEditing(false);
                        }}
                        disabled={isInteractionLocked}
                      >
                        {inlineLatexEditorOpen ? "Show statement" : "Edit expression"}
                      </button>
                    ) : null}
                  </div>
                )}
                <div
                  className={`problem-panel__latex-inline${
                    previewLength > 160
                      ? " problem-panel__latex-inline--long"
                      : previewLength > 90
                      ? " problem-panel__latex-inline--medium"
                      : previewLength > 0 && previewLength <= 60
                      ? " problem-panel__latex-inline--short"
                      : ""
                  }${
                    previewMode === "statement"
                      ? " problem-panel__latex-inline--statement"
                      : ""
                  }`}
                >
                  {statementEditing ? (
                    <div className="problem-panel__statement-editor">
                      <textarea
                        rows={6}
                        value={statementDraft}
                        onChange={(e) => setStatementDraft(e.target.value)}
                        placeholder="Type or edit the full problem statement."
                        disabled={isInteractionLocked}
                      />
                      <div className="cluster" style={{ gap: "8px", marginTop: "8px" }}>
                        <button
                          type="button"
                          className="btn btn--primary btn--small"
                          onClick={() => void handleStatementSave()}
                          disabled={isInteractionLocked}
                        >
                          Save statement
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost btn--small"
                          onClick={handleStatementCancel}
                          disabled={isInteractionLocked}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : inlineLatexEditorOpen && previewLatexValue ? (
                    <MathEditor
                      value={previewLatexValue}
                      onChange={handleInlineLatexChange}
                      onCommit={handleInlineLatexCommit}
                      readOnly={isInteractionLocked}
                      className={`problem-panel__math-editor${
                        isInteractionLocked ? " problem-panel__math-editor--locked" : ""
                      }`}
                      ariaLabel="Problem expression"
                    />
                  ) : statementDraft?.trim() ? (
                    <div className="problem-panel__statement">
                      {renderStatementParts()}
                    </div>
                  ) : previewLatexValue ? (
                    <MathEditor
                      value={previewLatexValue}
                      onChange={handleInlineLatexChange}
                      onCommit={handleInlineLatexCommit}
                      readOnly={isInteractionLocked}
                      className={`problem-panel__math-editor${
                        isInteractionLocked ? " problem-panel__math-editor--locked" : ""
                      }`}
                      ariaLabel="Problem expression"
                    />
                  ) : (
                    <span className="mini-label">No LaTeX yet.</span>
                  )}
                </div>
              </div>
            </div>
          )}
          {statusTokens.length > 0 && (
            <div className="problem-panel__status">
              <p className="mini-label" style={{ margin: 0 }}>
                Status: {statusTokens.join(" · ")}
              </p>
            </div>
          )}
          {showOcrVerification && (
            <div className="problem-panel__verify">
              <div>
                <p style={{ margin: 0, fontWeight: 600 }}>OCR check</p>
                <p className="mini-label" style={{ margin: 0 }}>
                  Does the rendered math match the image?
                </p>
              </div>
              <div className="cluster" style={{ gap: "8px" }}>
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={handleLatexConfirmed}
                  disabled={isInteractionLocked}
                >
                  Looks good
                </button>
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={handleLatexMismatch}
                  disabled={isInteractionLocked}
                >
                  Doesn't match
                </button>
              </div>
            </div>
          )}
          {showGatePanel && renderAttemptGate()}
          {showGateNotice && !showGatePanel && gateNotice && (
            <div className="problem-panel__gate card-quiet">
              <p className="mini-label" style={{ margin: 0 }}>
                {gateNotice}
              </p>
            </div>
          )}
          {canVisualize && (
            <div className="problem-panel__visualize card-quiet">
              <div className="cluster" style={{ gap: "12px", justifyContent: "space-between" }}>
                <div>
                  <p style={{ margin: 0, fontWeight: 600 }}>Visualize</p>
                  <p className="mini-label" style={{ margin: 0 }}>
                    Plot the extracted expression in Desmos.
                  </p>
                </div>
                <button
                  type="button"
                  className="btn btn--ghost btn--small"
                  onClick={() => setShowVisualize((prev) => !prev)}
                  disabled={isInteractionLocked}
                >
                  {showVisualize ? "Hide graph" : "Visualize"}
                </button>
              </div>
              {showVisualize && (
                <div style={{ marginTop: "10px" }}>
                  {desmosRequesting && (
                    <p className="mini-label" style={{ margin: "0 0 8px" }}>
                      Generating graph suggestions…
                    </p>
                  )}
                  {desmosRequestError && (
                    <p className="mini-label" style={{ margin: "0 0 8px", color: "#f7b5b5" }}>
                      {desmosRequestError}
                    </p>
                  )}
                  {desmosExpressions.length > 0 ? (
                    <DesmosGraph
                      expressions={desmosExpressions}
                      testId="desmos-graph"
                    />
                  ) : (
                    <p className="mini-label" style={{ margin: 0 }}>
                      No plottable expression yet.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          <div className="problem-panel__guidance card-quiet">
            <div className="cluster" style={{ gap: "12px", justifyContent: "space-between" }}>
              <div>
                <p style={{ margin: 0, fontWeight: 600 }}>Guidance</p>
                <p className="mini-label" style={{ margin: 0 }}>
                  {guidanceModeLabel}
                </p>
              </div>
              <button
                type="button"
                className="btn btn--ghost btn--small"
                onClick={() => setGuidanceOpen((prev) => !prev)}
                disabled={guidanceDisabled}
              >
                {guidanceOpen ? "Hide guidance" : "Ask for guidance"}
              </button>
            </div>
            {guidanceDisabledReason && (
              <p className="mini-label" style={{ margin: "8px 0 0" }}>
                {guidanceDisabledReason}
              </p>
            )}
            {guidanceOpen && (
              <div style={{ marginTop: "10px" }}>
                <Chat
                  conversation={guidanceConversation}
                  onSendMessage={handleGuidanceSend}
                  disabled={guidanceDisabled}
                  busy={guidanceBusy}
                  placeholder={
                    guidanceMode === CHAT_MODES.TUTOR
                      ? "Ask about the solution or alternatives..."
                      : "Ask where to focus next..."
                  }
                  disabledPlaceholder="Guidance is disabled."
                  disabledReason={guidanceError}
                />
              </div>
            )}
          </div>
          {showLatexDetails && (
            <div className="problem-panel__latex">
              <div className="task-form__field">
                <label>Extracted text</label>
                <div className="latex-card">
                  <textarea
                    rows={3}
                    value={problem?.problemText || ""}
                    readOnly
                    className="latex-card__textarea"
                    placeholder="No extracted text yet."
                  />
                  <button
                    type="button"
                    className="btn btn--ghost latex-card__copy"
                    onClick={() => handleCopyLatex(problem?.problemText || "")}
                    disabled={!String(problem?.problemText || "").trim()}
                  >
                    Copy text
                  </button>
                </div>
              </div>

              {showStatementDetails && (
                <>
                  <div className="task-form__field">
                    <label>Statement (rendered)</label>
                    <div className="latex-card">
                      <div className="latex-card__content latex-card__content--statement">
                        <div className="problem-panel__statement problem-panel__statement--details">
                          {renderStatementParts()}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="task-form__field">
                    <label>Statement (source)</label>
                    <div className="latex-card latex-card--code">
                      <textarea
                        rows={3}
                        value={statementDraft}
                        onChange={(e) => setStatementDraft(e.target.value)}
                        readOnly={isInteractionLocked}
                        className="latex-card__textarea"
                        placeholder="No statement yet."
                      />
                      <button
                        type="button"
                        className="btn btn--ghost latex-card__copy"
                        onClick={() => handleCopyLatex(statementDraft)}
                        disabled={!statementDraft?.trim()}
                      >
                        Copy statement
                      </button>
                      <button
                        type="button"
                        className="btn btn--ghost latex-card__copy"
                        onClick={() => void handleStatementSave()}
                        disabled={isInteractionLocked}
                      >
                        Save statement
                      </button>
                    </div>
                  </div>
                </>
              )}

              {showExpressionDetails && (
                <>
                  <div className="task-form__field">
                    <label>{showStatementDetails ? "Expression (rendered)" : "LaTeX (rendered)"}</label>
                    <div className="latex-card">
                      <div className="latex-card__content latex-card__content--center">
                        <LatexRenderer latex={latex} />
                      </div>
                    </div>
                  </div>
                  <div className="task-form__field">
                    <label>{showStatementDetails ? "Expression (LaTeX)" : "LaTeX (code)"}</label>
                    <div className="latex-card latex-card--code">
                      <textarea
                        rows={3}
                        value={latex}
                        readOnly
                        className="latex-card__textarea"
                        placeholder="No LaTeX yet."
                      />
                      <button
                        type="button"
                        className="btn btn--ghost latex-card__copy"
                        onClick={() => handleCopyLatex(latex)}
                        disabled={!latex?.trim()}
                      >
                        Copy LaTeX
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          {showHintActionRow && (
            <div className="problem-panel__actions-row">
              {hintActionNotes && (
                <p className="mini-label" style={{ margin: 0 }}>
                  {hintActionNotes}
                </p>
              )}
              <div className="cluster" style={{ gap: "8px", justifyContent: "flex-end" }}>
                {isImageProblem && !showOcrVerification && (
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={handleLatexMismatch}
                    disabled={!latex?.trim() || isInteractionLocked}
                  >
                    These don't match
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn--success"
                  onClick={handleFinish}
                  disabled={actionDisabled}
                  title="Mark this problem as solved and move on to the next one."
                >
                  {saving ? "Saving…" : "Solved"}
                </button>
              </div>
            </div>
          )}
          {showSolvedActionRow && (
            <div className="problem-panel__actions-row">
              <p className="mini-label" style={{ margin: 0 }}>
                Mark this problem solved to move on.
              </p>
              <div className="cluster" style={{ gap: "8px", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  className="btn btn--success"
                  onClick={handleFinish}
                  disabled={actionDisabled}
                >
                  {saving ? "Saving…" : "Solved"}
                </button>
              </div>
            </div>
          )}

          {showReview && (
            <div className="problem-panel__review card-quiet">
              <div className="problem-panel__review-header">
                <div>
                  <p style={{ margin: 0, fontWeight: 700 }}>Commit review</p>
                  <p className="mini-label" style={{ margin: 0 }}>
                    Final answer, reasoning, and calibration check.
                  </p>
                </div>
                {reviewOpen && onCloseReview && (
                  <button
                    type="button"
                    className="btn btn--ghost btn--small"
                    onClick={() => onCloseReview(problem?.id, task?.id)}
                  >
                    Close review
                  </button>
                )}
              </div>
              <div className="problem-panel__review-grid">
                <div className="problem-panel__review-block">
                  <p className="mini-label" style={{ margin: 0 }}>
                    Final answer
                  </p>
                  <pre className="problem-panel__review-answer">
                    {commitSummary.answer || "—"}
                  </pre>
                </div>
                <div className="problem-panel__review-block">
                  <p className="mini-label" style={{ margin: 0 }}>
                    Reasoning (1 sentence)
                  </p>
                  <p className="problem-panel__review-text">
                    {commitSummary.reasoning || "—"}
                  </p>
                </div>
                <div className="problem-panel__review-block">
                  <p className="mini-label" style={{ margin: 0 }}>
                    Confidence
                  </p>
                  <p className="problem-panel__review-text">
                    {Number.isFinite(commitSummary.confidence)
                      ? `${Math.round(commitSummary.confidence)}%`
                      : "—"}
                  </p>
                </div>
              </div>
              {commitSummary.submittedAt && (
                <p className="mini-label" style={{ margin: 0 }}>
                  Committed {formatDate(commitSummary.submittedAt)}
                </p>
              )}
              {reviewAnswerCheck && (
                <div
                  className={`answer-result${
                    reviewAnswerCheck.correct === true
                      ? " answer-result--correct"
                      : reviewAnswerCheck.correct === false
                      ? " answer-result--incorrect"
                      : " answer-result--unknown"
                  }`}
                >
                  <p style={{ margin: 0, fontWeight: 700 }}>
                    {reviewAnswerCheck.correct === true
                      ? "Correct"
                      : reviewAnswerCheck.correct === false
                      ? "Not quite"
                      : "Unknown"}
                    {typeof reviewAnswerCheck.confidence === "number"
                      ? ` · ${Math.round(reviewAnswerCheck.confidence * 100)}%`
                      : ""}
                  </p>
                  {reviewAnswerCheck.feedback ? (
                    <p className="mini-label" style={{ margin: 0 }}>
                      {reviewAnswerCheck.feedback}
                    </p>
                  ) : null}
                </div>
              )}
              {canonicalSteps.length > 0 && (
                <div className="problem-panel__review-steps">
                  <p className="mini-label" style={{ margin: 0, fontWeight: 700 }}>
                    Wolfram steps summary
                  </p>
                  <ol className="problem-panel__review-steps-list">
                    {canonicalSteps.slice(0, 8).map((step, index) => (
                      <li key={step.id || `${index}-${step.description || ""}`}>
                        {step.description && (
                          <p className="problem-panel__review-text">
                            {step.description}
                          </p>
                        )}
                        {step.mathLatex && (
                          <div className="problem-panel__review-step-math">
                            <LatexRenderer latex={step.mathLatex} />
                          </div>
                        )}
                      </li>
                    ))}
                  </ol>
                  {canonicalSteps.length > 8 && (
                    <p className="mini-label" style={{ margin: 0 }}>
                      +{canonicalSteps.length - 8} more steps
                    </p>
                  )}
                </div>
              )}
              {suggestedCards.length > 0 && (
                <div className="problem-panel__review-suggestions">
                  <p className="mini-label" style={{ margin: 0, fontWeight: 700 }}>
                    Suggested cards
                  </p>
                  <div className="stack stack--dense">
                    {suggestedCards.map((card) => {
                      const isAdded = addedSuggestionIds.includes(card.id);
                      const isBusy = suggestionBusyId === card.id;
                      const cardLabel =
                        getFlashcardTypeLabel(card.type) ||
                        card.type.replace(/_/g, " ");
                      return (
                        <div key={card.id} className="card-quiet">
                          <p style={{ margin: 0, fontWeight: 600 }}>
                            {cardLabel}
                          </p>
                          <div className="mini-label" style={{ margin: "6px 0 0" }}>
                            {renderFlashcardText(card.front, `suggest-front-${card.id}`)}
                          </div>
                          <div className="mini-label" style={{ margin: "6px 0 0" }}>
                            {renderFlashcardText(card.back, `suggest-back-${card.id}`)}
                          </div>
                          <div className="cluster" style={{ gap: "8px", marginTop: "8px" }}>
                            <button
                              type="button"
                              className="btn btn--ghost btn--small"
                              onClick={() => handleAddSuggestion(card)}
                              disabled={!canAddMoreCards || isAdded || isBusy}
                            >
                              {isAdded ? "Added" : isBusy ? "Adding…" : "Add card"}
                            </button>
                            {!canAddMoreCards && !isAdded && (
                              <span className="mini-label">Limit reached</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {(showReview || hasUnlockedAnyHint) && (
            <div className="hint-manager-reveal">
              <HintManager
                problem={problem}
                hints={hints}
                revealAll={showReview}
                hintCheckResponses={hintCheckResponses}
                onSubmitCheck={handleHintCheckSubmit}
                isLocked={isInteractionLocked || showReview}
              />
            </div>
          )}

          {showHintRequestRow && (
            <div className="problem-panel__hint-actions card-quiet">
              <div className="cluster" style={{ gap: "10px", flexWrap: "wrap" }}>
                <span style={{ fontWeight: 600 }}>
                  Next hint: Level {nextHintLevel}
                </span>
                <button
                  type="button"
                  className="btn btn--ghost btn--small"
                  onClick={() => void handleRequestHint()}
                  disabled={
                    isInteractionLocked ||
                    hintRequesting ||
                    !nextHintTimeReady ||
                    !nextHintCheckReady
                  }
                >
                  {hintRequesting
                    ? "Requesting..."
                    : hasPrefetchedNextHint
                    ? `Unlock hint ${nextHintLevel}`
                    : `Request hint ${nextHintLevel}`}
                </button>
              </div>
              <div className="mini-label" style={{ marginTop: "6px" }}>
                {nextHintUnlockNote}
              </div>
              {hintRequestError && (
                <div className="mini-label" style={{ marginTop: "6px", color: "#fca5a5" }}>
                  {hintRequestError}
                </div>
              )}
            </div>
          )}

          <details className="problem-panel__section">
            <summary>Details</summary>
            <div className="task-form__row">
              <div className="task-form__field">
                <label>Problem type</label>
                <select
                  value={problemType}
                  onChange={(e) => {
                    const nextType = e.target.value;
                    setProblemType(nextType);
                    const [firstApproach] = getApproachesForType(nextType);
                    setApproach(firstApproach || "Other");
                  }}
                >
                  {PROBLEM_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
              <div className="task-form__field">
                <label>Approach</label>
                <select
                  value={approach}
                  onChange={(e) => setApproach(e.target.value)}
                >
                  {getApproachesForType(problemType).map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>
              <div className="task-form__field" style={{ maxWidth: "160px" }}>
                <label>Difficulty</label>
                <input
                  type="number"
                  min={1}
                  max={5}
                  value={difficultyScore}
                  onChange={(e) => setDifficultyScore(Number(e.target.value) || 1)}
                />
              </div>
              <button
                type="button"
                className="btn btn--ghost"
                style={{ alignSelf: "flex-end" }}
                onClick={handleSaveMetadata}
                disabled={actionDisabled}
              >
                {saving ? "Saving…" : "Save details"}
              </button>
            </div>

            <div className="task-form__row">
              <div className="task-form__field">
                <label>Notes (type)</label>
                <input
                  type="text"
                  value={whyType}
                  onChange={(e) => setWhyType(e.target.value)}
                />
              </div>
              <div className="task-form__field">
                <label>Notes (approach)</label>
                <input
                  type="text"
                  value={whyApproach}
                  onChange={(e) => setWhyApproach(e.target.value)}
                />
              </div>
            </div>

          </details>

          <details className="problem-panel__section" open>
            <summary>Struggle log</summary>
            {hintTimeline.length === 0 && (
              <p className="mini-label">No hints or notes logged yet.</p>
            )}
            <div className="stack stack--dense">
              {hintTimeline.map((entry, index) => (
                <div key={`${entry.timestamp}-${index}`} className="card-quiet">
                  <p style={{ margin: 0, fontWeight: 600 }}>
                    {typeof entry.level === "number"
                      ? `Hint level ${entry.level}`
                      : "Note"}
                  </p>
                  {entry.resourceId && (
                    <p className="mini-label" style={{ margin: 0 }}>
                      Resource: {entry.resourceId}
                    </p>
                  )}
                  {entry.note && (
                    <p className="mini-label" style={{ margin: 0 }}>
                      {entry.note}
                    </p>
                  )}
                  <p className="mini-label" style={{ margin: 0 }}>
                    {formatDate(entry.timestamp)}
                  </p>
                </div>
              ))}
            </div>
          </details>

          <details className="problem-panel__section">
            <summary>Answer check (optional)</summary>
            <div className="stack stack--dense" style={{ gap: "10px" }}>
              <p className="mini-label" style={{ margin: 0 }}>
                Paste your written answer as an image or LaTeX, then optionally check it
                against the canonical solution.
              </p>

              <div onPaste={handleAnswerPasteEvent}>
                {answerImage ? (
                  <div className="problem-panel__preview" style={{ marginBottom: "8px" }}>
                    <p className="mini-label" style={{ margin: 0 }}>
                      Answer image
                    </p>
                    <div className="problem-panel__preview-body">
                      <img
                        src={URL.createObjectURL(answerImage)}
                        alt="Answer"
                        style={{ maxWidth: "100%", borderRadius: "10px" }}
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn--ghost btn--small"
                      onClick={() => setAnswerImage(null)}
                      style={{ marginTop: "4px", alignSelf: "flex-start" }}
                    >
                      Remove image
                    </button>
                  </div>
                ) : null}
              </div>

              <div className="task-form__field">
                <label>Your answer (LaTeX)</label>
                <textarea
                  rows={3}
                  value={studentAnswerLatex}
                  onChange={(e) => {
                    setStudentAnswerLatex(e.target.value);
                    setAnswerCheck(null);
                  }}
                  placeholder="Paste LaTeX here, or extract it from an answer image."
                />
              </div>

              <div className="cluster" style={{ gap: "8px" }}>
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={handlePasteAnswer}
                  disabled={answerBusy || isInteractionLocked}
                >
                  Paste answer
                </button>
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={handleExtractAnswerLatex}
                  disabled={
                    !answerImage ||
                    answerBusy ||
                    !onOcrLatexFromImage ||
                    isInteractionLocked
                  }
                  title={
                    onOcrLatexFromImage
                      ? ""
                      : "Answer OCR is not available in this build."
                  }
                >
                  {answerBusy ? "Working…" : "Extract LaTeX"}
                </button>
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={handleCheckAnswer}
                  disabled={
                    !String(studentAnswerLatex || "").trim() ||
                    answerBusy ||
                    !onCheckAnswerForProblem ||
                    isInteractionLocked
                  }
                >
                  {answerBusy ? "Working…" : "Check answer"}
                </button>
              </div>

              {problem?.canonicalSolution?.finalAnswer && (
                <div className="card-quiet" style={{ padding: "12px 14px" }}>
                  <p className="mini-label" style={{ margin: 0, fontWeight: 700 }}>
                    Wolfram final answer (plaintext)
                  </p>
                  <pre style={{ margin: "8px 0 0", whiteSpace: "pre-wrap" }}>
                    {problem.canonicalSolution.finalAnswer}
                  </pre>
                </div>
              )}

              {answerCheck && (
                <div
                  className={`answer-result${
                    answerCheck.correct === true
                      ? " answer-result--correct"
                      : answerCheck.correct === false
                      ? " answer-result--incorrect"
                      : " answer-result--unknown"
                  }`}
                >
                  <p style={{ margin: 0, fontWeight: 700 }}>
                    {answerCheck.correct === true
                      ? "Correct"
                      : answerCheck.correct === false
                      ? "Not quite"
                      : "Unknown"}
                    {typeof answerCheck.confidence === "number"
                      ? ` · ${Math.round(answerCheck.confidence * 100)}%`
                      : ""}
                  </p>
                  {answerCheck.feedback ? (
                    <p className="mini-label" style={{ margin: 0 }}>
                      {answerCheck.feedback}
                    </p>
                  ) : null}
                </div>
              )}
            </div>
          </details>

          <details className="problem-panel__section">
            <summary>Reflection</summary>
            <div className="task-form__field">
              <label>Final reflection</label>
              <textarea
                rows={3}
                value={finalReflection}
                onChange={(e) => setFinalReflection(e.target.value)}
                placeholder="E1–E3 reflection"
              />
            </div>
            <div className="task-form__row">
              <div className="task-form__field">
                <label>Mistake tags</label>
                <input
                  type="text"
                  value={mistakeTags}
                  onChange={(e) => setMistakeTags(e.target.value)}
                  placeholder="Comma-separated, e.g., wrong u choice, algebra slip"
                />
              </div>
              <div className="task-form__field" style={{ maxWidth: "160px" }}>
                <label>Reflection confidence (1–5)</label>
                <input
                  type="number"
                  min={1}
                  max={5}
                  value={confidence}
                  onChange={(e) => setConfidence(Number(e.target.value) || 1)}
                />
              </div>
              <label className="checkbox-field" style={{ alignSelf: "flex-end" }}>
                <input
                  type="checkbox"
                  checked={autoComplete}
                  onChange={() => setAutoComplete((prev) => !prev)}
                />
                Auto-complete task on solve
              </label>
            </div>
          </details>
        </div>
      )}

      {commitOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal modal--commit">
            <h3>Commit your answer</h3>
            <p className="mini-label" style={{ margin: "0 0 12px" }}>
              Submit a final answer and a one-sentence reason before reveal.
            </p>
            <div className="task-form__field">
              <label>Final answer (text or LaTeX)</label>
              <textarea
                rows={3}
                value={commitAnswer}
                onChange={(event) => setCommitAnswer(event.target.value)}
                placeholder="e.g., $6\\arctan(2/3) - \\pi + 2\\ln(52/32)$"
                aria-label="Final answer"
                disabled={commitSubmitting || isInteractionLocked}
              />
            </div>
            <div className="task-form__field">
              <label>Reasoning (1 sentence)</label>
              <textarea
                rows={2}
                value={commitReasoning}
                onChange={(event) => setCommitReasoning(event.target.value)}
                placeholder="1 sentence"
                aria-label="Commit reasoning"
                disabled={commitSubmitting || isInteractionLocked}
              />
            </div>
            <div className="task-form__field">
              <label>Confidence</label>
              <div className="commit-slider">
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={commitConfidence}
                  onChange={(event) => setCommitConfidence(Number(event.target.value))}
                  aria-label="Commit confidence"
                  disabled={commitSubmitting || isInteractionLocked}
                />
                <span>{Math.round(commitConfidence)}%</span>
              </div>
            </div>
            {commitError && (
              <p className="mini-label" style={{ margin: 0, color: "#fca5a5" }}>
                {commitError}
              </p>
            )}
            <div className="modal-actions">
              <button
                className="btn btn--primary"
                onClick={handleCommitSubmit}
                disabled={commitSubmitting || isInteractionLocked}
              >
                {commitSubmitting ? "Submitting…" : "Commit & reveal"}
              </button>
              <button
                className="btn btn--ghost"
                onClick={() => setCommitOpen(false)}
                disabled={commitSubmitting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProblemPanel;
